import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';
import { TourId } from 'webapp/enums/tour';

import didInsert from 'webapp/modifiers/did-insert';
import CommonPopupsPopupBlueBubble from 'webapp/components/common/popups/popup-blue-bubble';
import ScriptsFormCode from 'webapp/components/scripts/form/code';
import ScriptsFormPopupOptionsMathComponent from 'webapp/components/scripts/form/popup-options-math';
import ScriptsFormPopupOptionsMetricComponent from 'webapp/components/scripts/form/popup-options-metric';
import SubmitButton from 'webapp/components/submit-button';

import type { Script } from 'webapp/models-custom';
import type { ExpressionDetailItem } from 'webapp/models-custom/script';

interface Args {
	onItemSelect: (model: Script) => void;
}

export default class CustomMetricsEditLandingScriptComponent extends Component<Args> {
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;
	@service('billing') declare billingService: Service.Billing;
	@service('popup') declare popupService: Service.Popup;
	@service('tour') declare tourService: Service.Tour;

	model: Script;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.model = this.mmScriptsService.createNewModel();
	}

	get showUpgrade() {
		return !this.billingService.scriptsEnabled || this.notEnoughCustomMetricsLimit;
	}

	get notEnoughCustomMetricsLimit() {
		return !this.billingService.checkIfCan(BillingFeature.CUSTOM_METRICS);
	}

	@action
	upgrade() {
		if (!this.billingService.scriptsEnabled) {
			this.billingService.handleLimitations(BillingFeature.SCRIPTS);
		} else {
			this.billingService.handleLimitations(BillingFeature.CUSTOM_METRICS);
		}
	}

	@action
	setupView(): void {
		later(() => {
			if (
				!this.showUpgrade &&
				!this.popupService.isOpen(CommonPopupsPopupBlueBubble) &&
				this.tourService.isEligibleForCustomMetricsPopupScriptTour
			) {
				this.tourService.initTour(TourId.CUSTOM_METRICS_POPUP_SCRIPT)?.start();
			}
		}, 600);
	}

	@action
	updateExpression(expression: string, expressionDetails: ExpressionDetailItem[]) {
		this.model.expression = expression;
		this.model.expressionDetails = expressionDetails;
		this.args.onItemSelect(this.model);

		// explicitly close all script math or metric popups
		[
			this.popupService.findByComponent(ScriptsFormPopupOptionsMetricComponent),
			this.popupService.findByComponent(ScriptsFormPopupOptionsMathComponent),
			this.popupService.findByComponent(CommonPopupsPopupBlueBubble),
		].forEach((p) => {
			if (p) {
				void p.close();
			}
		});
	}

	<template>
		<div class="landing-side landing-side--script" {{didInsert this.setupView}}>
			<h4>
				With calculations
				<p>Use formulas to calculate a new metric</p>
			</h4>

			<div class="empty-placeholder clear">
				Start by adding a metric<br />
				to the calculation
			</div>

			<ScriptsFormCode @model={{this.model}} @updateExpression={{this.updateExpression}} />

			{{#if this.showUpgrade}}
				<div class="blur"></div>
				<SubmitButton @text="Upgrade" @size="big" @onTrigger={{this.upgrade}} />
			{{/if}}
		</div>
	</template>
}
